import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import type { ShapeData } from '@/lib/shapes/types/shape-data'

import { clampToLiveAreaX, clampToLiveAreaY } from '../helpers/clamp-to-live-area'
import { resolveManualDisplayPos } from '../helpers/manual-display-pos'
import type { ManualAnchor } from '../types/manual-anchor'
import type { ScreenPoint, ScreenRect } from '../types/screen-rect'

export interface DraggableToolbarAnchorOptions {
  shape: ShapeData
  scale: number
  canvasRef: React.RefObject<HTMLDivElement | null>
  /** The toolbar's smart-placed position (`useFloatingChromePosition`). */
  pos: ScreenPoint | null
  /** The toolbar's approximate rendered size, for clamping and the nudge check. */
  width: number
  height: number
  /** The shape rect the manually-placed toolbar is nudged clear of. */
  getShapeRect: (shape: ShapeData, scale: number, canvasRect: DOMRect) => ScreenRect
  /**
   * `true`: `cancelActiveDrag` reverts the toolbar to exactly where it was before the drag
   * started. `false`: it stops the drag in place (the toolbar stays where it was last dragged to —
   * `manualAnchor` already reflects that position, so no revert is needed).
   */
  revertOnCancel: boolean
}

/**
 * The grip-handle drag shared by the floating toolbars: the manual anchor (see `ManualAnchor`),
 * the pointer handlers that set it, the reset when a different shape enters the mode, and the
 * Escape cancel. Returns `displayPos` — the manual position when there is one, else `pos`.
 */
export function useDraggableToolbarAnchor({
  shape,
  scale,
  canvasRef,
  pos,
  width,
  height,
  getShapeRect,
  revertOnCancel,
}: DraggableToolbarAnchorOptions) {
  const [manualAnchor, setManualAnchor] = useState<ManualAnchor | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const isDraggingRef = useRef(false)
  const dragOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  const dragPointerRef = useRef<{ el: HTMLButtonElement; pointerId: number } | null>(null)
  // Snapshot of `manualAnchor` from just before the current drag started — restored verbatim by
  // `cancelActiveDrag` (Escape) when `revertOnCancel` is set, so a cancelled drag reverts, rather
  // than confirming wherever the pointer happened to be when Escape was pressed.
  const dragStartManualAnchorRef = useRef<ManualAnchor | null>(null)

  // Reset manual placement when a different shape enters the mode.
  useEffect(() => {
    setManualAnchor(null)
  }, [shape.id])

  let displayPos = pos

  if (manualAnchor) {
    const canvasEl = canvasRef.current

    if (canvasEl) {
      const canvasRect = canvasEl.getBoundingClientRect()

      displayPos = resolveManualDisplayPos(
        manualAnchor,
        canvasRect,
        scale,
        width,
        height,
        getShapeRect(shape, scale, canvasRect)
      )
    }
  }

  const handleDragStart = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!displayPos) return
    dragOffsetRef.current = {
      x: e.clientX - displayPos.x,
      y: e.clientY - displayPos.y,
    }
    dragStartManualAnchorRef.current = manualAnchor
    isDraggingRef.current = true
    setIsDragging(true)
    dragPointerRef.current = { el: e.currentTarget, pointerId: e.pointerId }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const handleDragMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!isDraggingRef.current) return
    const canvasEl = canvasRef.current

    if (!canvasEl) return
    e.preventDefault()
    const canvasRect = canvasEl.getBoundingClientRect()
    const newX = clampToLiveAreaX(e.clientX - dragOffsetRef.current.x, width)
    const newY = clampToLiveAreaY(e.clientY - dragOffsetRef.current.y, height)
    // Anchor to the shape's current center in canvas-local (document) coordinates — a frozen
    // snapshot, not a live reference — so future reshaping via node edits doesn't move the
    // toolbar, while future pan/zoom (which re-read canvasRect/scale on every render) do.
    const docX = shape.x + shape.width / 2
    const docY = shape.y + shape.height / 2

    setManualAnchor({
      docX,
      docY,
      offsetX: newX - (canvasRect.left + docX * scale),
      offsetY: newY - (canvasRect.top + docY * scale),
    })
  }

  const handleDragEnd = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!isDraggingRef.current) return
    isDraggingRef.current = false
    setIsDragging(false)
    dragPointerRef.current = null
    e.currentTarget.releasePointerCapture(e.pointerId)
  }

  // Reads only refs and state setters, so it is stable — the caller's imperative handle is
  // created once, exactly as when it was declared inline with `[]` deps.
  const cancelActiveDrag = useCallback(() => {
    if (!isDraggingRef.current) return false

    isDraggingRef.current = false
    setIsDragging(false)
    if (revertOnCancel) setManualAnchor(dragStartManualAnchorRef.current)

    const captured = dragPointerRef.current

    dragPointerRef.current = null
    if (captured?.el.hasPointerCapture(captured.pointerId)) {
      captured.el.releasePointerCapture(captured.pointerId)
    }

    return true
  }, [revertOnCancel])

  return {
    displayPos,
    isDragging,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    cancelActiveDrag,
  }
}
